import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { GraduationCap, BookOpen, Clock, Coins, Star, Search, Play, CheckCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function CourseCard({ course, isCompleted, onComplete, isAuth }) {
  const diffColor = course.difficulty === 'beginner' ? 'bg-green-500/20 text-green-400' : course.difficulty === 'intermediate' ? 'bg-blue-500/20 text-blue-400' : 'bg-purple-500/20 text-purple-400';
  return (
    <div className="course-card" data-testid={`course-card-${course.id}`}>
      <div className="relative">
        <img src={course.image_url} alt={course.title} className="w-full h-48 object-cover" />
        {isCompleted && <div className="absolute top-3 right-3 bg-green-500 text-white px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1"><CheckCircle size={14} /> Done</div>}
      </div>
      <div className="p-5">
        <div className="flex items-center gap-2 mb-3">
          <span className={`text-xs px-2 py-1 rounded-full ${diffColor}`}>{course.difficulty}</span>
          <span className="text-xs text-white/40 capitalize">{course.category}</span>
        </div>
        <h3 className="font-unbounded text-lg font-bold text-white mb-2">{course.title}</h3>
        <p className="text-sm text-white/60 mb-4 line-clamp-2">{course.description}</p>
        <div className="flex gap-4 mb-4 text-xs text-white/50">
          <div className="flex items-center gap-1"><Clock size={14} /> {course.duration_hours}h</div>
          <div className="flex items-center gap-1"><BookOpen size={14} /> {course.lessons_count} lessons</div>
        </div>
        <div className="flex gap-4 mb-4 py-3 border-t border-white/10">
          <div className="flex items-center gap-1"><Coins size={16} className="text-[#D4AF37]" /><span className="text-[#D4AF37] font-semibold">+{course.token_reward} AIH</span></div>
          <div className="flex items-center gap-1"><Star size={16} className="text-blue-400" /><span className="text-blue-400 font-semibold">+{course.xp_reward} XP</span></div>
        </div>
        {isCompleted ? (
          <Button className="w-full bg-green-500/20 text-green-400" disabled><CheckCircle className="mr-2" size={18} /> Completed</Button>
        ) : (
          <Button className="w-full btn-primary" onClick={() => onComplete(course.id)} data-testid={`complete-course-${course.id}`}>
            <Play className="mr-2" size={18} /> {isAuth ? 'Start Course' : 'Login to Start'}
          </Button>
        )}
      </div>
    </div>
  );
}

const Academy = () => {
  const { user, token, isAuthenticated, updateUser } = useAuth();
  const [courses, setCourses] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('all');
  const [loading, setLoading] = useState(true);

  const categories = ['all', 'web3', 'ai', 'marketing', 'saas', 'finance', 'entrepreneurship'];

  useEffect(() => {
    axios.get(`${API}/courses`).then(r => { setCourses(r.data); setFiltered(r.data); }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    let f = courses;
    if (category !== 'all') f = f.filter(c => c.category === category);
    if (search) f = f.filter(c => c.title.toLowerCase().includes(search.toLowerCase()));
    setFiltered(f);
  }, [search, category, courses]);

  const completeCourse = async (id) => {
    if (!isAuthenticated) return toast.error('Please login');
    try {
      const r = await axios.post(`${API}/courses/${id}/complete`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`+${r.data.tokens_earned} AIH, +${r.data.xp_earned} XP`);
      updateUser({ aih_tokens: (user.aih_tokens || 0) + r.data.tokens_earned, xp_points: (user.xp_points || 0) + r.data.xp_earned, completed_courses: [...(user.completed_courses || []), id] });
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="spinner" /></div>;

  return (
    <div className="min-h-screen pt-20 pb-12 px-6" data-testid="academy-page">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="font-unbounded text-3xl font-bold text-white mb-2 flex items-center gap-3"><GraduationCap className="text-[#D4AF37]" /> Learning Academy</h1>
          <p className="text-white/60">Learn Web3, AI, Marketing & more. Earn tokens for completing courses.</p>
        </div>

        <div className="flex flex-col md:flex-row gap-4 mb-8">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" size={18} />
            <Input placeholder="Search courses..." value={search} onChange={(e) => setSearch(e.target.value)} className="input-cosmic pl-10" data-testid="course-search" />
          </div>
          <div className="flex flex-wrap gap-2">
            {categories.map(c => (
              <Button key={c} variant={category === c ? 'default' : 'ghost'} className={category === c ? 'btn-primary' : 'text-white/60'} onClick={() => setCategory(c)} data-testid={`category-${c}`}>
                {c === 'all' ? 'All' : c.charAt(0).toUpperCase() + c.slice(1)}
              </Button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map(c => <CourseCard key={c.id} course={c} isCompleted={user?.completed_courses?.includes(c.id)} onComplete={completeCourse} isAuth={isAuthenticated} />)}
        </div>

        {filtered.length === 0 && <div className="text-center py-16"><GraduationCap size={48} className="mx-auto text-white/20 mb-4" /><p className="text-white/60">No courses found.</p></div>}
      </div>
    </div>
  );
};

export default Academy;
